import { useMemo, useRef } from 'react';

import { useTheme } from '@mui/material';

import useChartFunctions, {
  getStandardWatermarkAttributes,
  getWatermarkConfig
} from '@/components/Chart/hooks/options/useChartFunctions';
import type { PreviewChartProps } from '@/features/DataHub/constants/charts';
import useUserDetails from '@/hooks/useUserDetails';

import PreviewChartLayout from './PreviewChartLayout';
import usePreviewOptions from './usePreviewOptions';
import useSpotChartData from './useSpotChartData';

import type { XAxisOptions } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

const PreviewFullscreenChart = ({ symbols, showLegend, isLightMode }: PreviewChartProps) => {
  const { loading, error, chartSeries, baseOptions, xAxisMin, xAxisMax } =
    useSpotChartData(symbols);
  const theme = useTheme();
  const { isUserBasic, isUserExpired } = useUserDetails();
  const isUserExpiredValue = Boolean(isUserExpired);
  const textColor = isLightMode ? theme.palette.background.paper : theme.palette.text.primary;
  const backgroundColor = isLightMode ? theme.palette.text.primary : theme.palette.background.paper;
  const previewOptions = usePreviewOptions(baseOptions, textColor, backgroundColor);
  const { createEventOptions } = useChartFunctions();
  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);
  const watermarkImageName = isLightMode ? '/watermark-color.svg' : '/watermark-black.svg';
  const { aspectRatio } = getWatermarkConfig(watermarkImageName);

  const getWatermarkAttributes = (chart: Highcharts.Chart) => {
    return getStandardWatermarkAttributes({
      chart,
      aspectRatio,
      isUserBasic,
      isUserExpired: isUserExpiredValue
    });
  };
  const getWatermarkImageRef = () => watermarkImage;
  const { events } = createEventOptions(
    getWatermarkImageRef,
    getWatermarkAttributes,
    watermarkImageName
  );
  const options = useMemo(() => {
    if (!previewOptions) return undefined;

    return {
      ...previewOptions,
      chart: { ...previewOptions.chart, events },
      legend: { ...previewOptions.legend, enabled: showLegend },
      xAxis: {
        ...previewOptions.xAxis,
        labels: {
          ...(previewOptions?.xAxis as XAxisOptions).labels,
          style: { ...(previewOptions?.xAxis as XAxisOptions).labels?.style, color: textColor }
        },
        min: xAxisMin,
        max: xAxisMax
      }
    };
  }, [previewOptions, showLegend, textColor, xAxisMin, xAxisMax, events]);

  return (
    <PreviewChartLayout
      chartSeries={chartSeries}
      chartType='spot'
      error={error}
      loading={loading}
      options={options}
    />
  );
};

export default PreviewFullscreenChart;
